import { ref, watch, onMounted, nextTick, type Ref } from 'vue'
import { trackEvent } from '~/composables/analytics'
import { responseError } from '~/utils/responseError'
import { toDisplayError, displayError, BAD_PAYLOAD_MESSAGE } from '~/utils/errorMessage'
import { response } from '~/assets/js/colorData.js'
import type { FeedbackTarget } from '~/composables/useResultFeedback'

interface GenerationDeps {
  screenshotLoaded: Ref<boolean>
  screenshotAnimation: Ref<boolean>
  scrollElement: Ref<any>
  title: Ref<string>
  ensureLottie: () => void
  resetFiltering: () => void
}

// The palette generation engine: URL input, human verification (Cloudflare
// Turnstile), the request to the color API, and all the associated loading /
// error state. Deep links (?url=…) are handled here too. Behavior is preserved
// exactly from the original page.
export function usePaletteGeneration(deps: GenerationDeps) {
  const { screenshotLoaded, screenshotAnimation, scrollElement, title, ensureLottie, resetFiltering } = deps

  const route = useRoute()
  const router = useRouter()
  const queryParam = ref(route.query)

  const urlData = ref('')
  const typedUrl = ref('')
  const hasError = ref(false)
  const hasWarning = ref(false)
  const hasSuccess = ref(false)
  // Always a plain, display-ready string (see setError) — never an Error or a
  // response body, so the template can render it directly.
  const errorMsg = ref<string>('')
  const isLoading = ref(false)
  const isRequestInProgress = ref(false)
  // What <ResultFeedbackBar> is currently offering to rate. Set only from a
  // rendered palette's own `meta` (see below), and cleared the moment a new
  // generation starts or one fails, so the prompt can never end up attached to
  // a result that is no longer on screen.
  const feedbackTarget = ref<FeedbackTarget | null>(null)
  const settingToggle = ref(false)

  const mailingLists = ref([
    { id: 1, title: 'Brand', description: 'Smart brand colors auto-detected from the site', checked: true, sliderCapable: false },
    { id: 2, title: 'Jpg Screenshot', description: 'Colors from homepage', checked: true, sliderCapable: true, currentVal: 6 },
    { id: 3, title: 'Html', description: "Colors from the site's HTML", checked: true, sliderCapable: false },
    { id: 4, title: 'Css', description: 'Colors from all CSS files', checked: true, sliderCapable: false },
    { id: 5, title: 'Js', description: 'Colors from all JS files', checked: true, sliderCapable: false },
  ])

  // --- Human verification (Cloudflare Turnstile) ---------------------------
  const token = ref<string | null>(null)
  const turnstileRef = ref<any>(null)
  const setTurnstileRef = (el: any) => {
    turnstileRef.value = el
  }
  // The user pressed "Generate" before a token was ready — continue automatically.
  const pendingGenerate = ref(false)
  const turnstileFailed = ref(false)
  let pendingTimeoutId: ReturnType<typeof setTimeout>
  let pendingHintId: ReturnType<typeof setTimeout>

  // Options passed straight to the Cloudflare widget. `interaction-only` keeps
  // it invisible unless a challenge is actually required; error-callback
  // returning true tells Turnstile the transient error is handled (stops the
  // throws that flooded Sentry); expired-callback drops the stale token.
  const turnstileOptions = {
    appearance: 'interaction-only',
    'error-callback': () => {
      turnstileFailed.value = true
      if (pendingGenerate.value) {
        hasError.value = false
        hasWarning.value = true
        errorMsg.value = 'Verification is taking a little longer — hang tight…'
      }
      return true
    },
    'expired-callback': () => {
      token.value = null
      turnstileRef.value?.reset?.()
    },
  }

  // Color extraction is served by the same-origin Nitro adapter (server/api/getColors).
  const runtimeConfig = useRuntimeConfig()
  const baseUrl = `${runtimeConfig.public.colorApiBase || ''}/api/getColors?url=`

  const resetPendingVerification = () => {
    pendingGenerate.value = false
    clearTimeout(pendingTimeoutId)
    clearTimeout(pendingHintId)
  }

  // Cloudflare Turnstile tokens are single-use: once the server verifies a token
  // (siteverify) it can't be validated again — a repeat returns
  // "timeout-or-duplicate". The widget only refreshes on expiry (~5 min), so
  // without this a second generation reuses the already-consumed token and fails
  // with "403 Human verification failed". Reset after every request so a fresh
  // token is issued for the next one; null the ref meanwhile so any generation
  // clicked in the gap is queued (getUrl) until the new token arrives rather than
  // sending the stale one.
  const refreshVerification = () => {
    token.value = null
    turnstileRef.value?.reset?.()
  }

  // The one place an error becomes something the user reads. Everything is put
  // through toDisplayError first, so `errorMsg` is always a short, markup-free
  // string — never an Error object, and never a response body (see
  // utils/errorMessage.ts). The original is logged for diagnosis.
  const setError = (error: unknown) => {
    if (error && typeof error === 'object') console.error('[colorize] request failed:', error)
    hasError.value = true
    hasWarning.value = false
    errorMsg.value = toDisplayError(error)
    isLoading.value = false
    // Nothing on screen worth rating, and the failed attempt must not leave the
    // previous palette's prompt up.
    feedbackTarget.value = null
  }

  // Guarantee the four visual-palette buckets always exist and hold arrays, so a
  // backend response missing a bucket can never reach the (unconditional) render.
  const normalizeVisualPalette = (vp: any) => {
    const src = vp && typeof vp === 'object' ? vp : {}
    const bucket = (k: string) => (Array.isArray(src[k]) ? src[k] : [])
    return {
      'Brand Colors': bucket('Brand Colors'),
      'Logo Colors': bucket('Logo Colors'),
      'Homepage Colors': bucket('Homepage Colors'),
      'Custom Colors': bucket('Custom Colors'),
    }
  }

  const fetchData = async (url: string) => {
    // Prevent duplicate requests
    if (isRequestInProgress.value) {
      console.log('Request already in progress, skipping duplicate')
      return
    }

    try {
      isRequestInProgress.value = true

      try {
        trackEvent('Generate Palette')
        ;(window as any).clarity('set', 'url', url)
      } catch {
        // Error handling for clarity
      }

      if (scrollElement.value) {
        scrollElement.value.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }

      // Forward the Turnstile token so the adapter can verify it when enforcement
      // is enabled (harmless no-op otherwise).
      const firstResponse = await fetch(url, token.value ? { headers: { 'x-turnstile-token': token.value } } : undefined)

      if (!firstResponse.ok) {
        throw await responseError(firstResponse)
      }

      // A 2xx is not a promise of JSON: a CDN can serve a cached HTML page (or
      // an interstitial) under this URL with a 200. Parsing defensively turns
      // that into the same clean "unexpected response" message instead of a
      // SyntaxError carrying the document in its text.
      let data: any
      try {
        data = await firstResponse.json()
      } catch {
        throw displayError(BAD_PAYLOAD_MESSAGE, firstResponse.status)
      }
      if (!data || typeof data !== 'object') {
        throw displayError(BAD_PAYLOAD_MESSAGE, firstResponse.status)
      }
      // Normalize the shape before it reaches the template.
      response.nearestColors = data.nearestColors && typeof data.nearestColors === 'object' ? data.nearestColors : {}
      response.visualPalette = normalizeVisualPalette(data.visualPalette)
      response.favicon = data.favicon
      response.screenshot = data.screenshot
      isLoading.value = false
      typedUrl.value = urlData.value
      // Offer the palette for rating. `meta` comes from the adapter
      // (server/utils/prefetch.ts) and carries the url exactly as it was sent
      // upstream plus the call's trace id — the two things the rating endpoint
      // needs to find the row this palette came from. An older cached response
      // without `meta` simply never prompts, rather than posting a url the
      // server would fail to resolve.
      feedbackTarget.value = data.meta?.url
        ? { url: String(data.meta.url), traceId: data.meta.requestId ?? null }
        : null
      router.push({ path: '/', query: { url: url } })
      title.value = 'Untitled Palette'
      // The screenshot's loading state is cleared by <ScreenshotSampler> once the
      // image loads (it also handles the cached same-src case), so nothing more
      // is needed here.
    } catch (error) {
      setError(error)
      screenshotAnimation.value = false
    } finally {
      isRequestInProgress.value = false
      // The token was consumed by this request (whether it succeeded or 403'd).
      // Issue a fresh one so the next URL can generate.
      refreshVerification()
    }
  }

  // Deep links (?url=...) should only auto-load once, even though the token
  // refreshes periodically. Declared here because startGenerate() below sets it.
  let queryParamHandled = false

  // Build the request URL from current settings and start generating. Assumes
  // input is validated and a Turnstile token is available.
  const startGenerate = () => {
    resetPendingVerification()
    // Any manual generation supersedes the ?url= deep link the page was opened
    // with. fetchData() pushes the generated URL into the query string, so
    // without this the fresh token issued by refreshVerification() would reach
    // the token watcher with queryParamHandled still false, look like a first
    // verification, and re-run the deep link on top of the result.
    queryParamHandled = true
    resetFiltering()
    hasError.value = false
    hasWarning.value = false
    isLoading.value = true
    // Take any prompt down for the duration: the palette it refers to is about
    // to be replaced.
    feedbackTarget.value = null
    ensureLottie()
    if (mailingLists.value.find((item) => item.id === 2)?.checked) {
      screenshotAnimation.value = true
    }
    const builtUrl =
      baseUrl +
      urlData.value +
      '&html=' + mailingLists.value.find((item) => item.id === 3)?.checked +
      '&css=' + mailingLists.value.find((item) => item.id === 4)?.checked +
      '&js=' + mailingLists.value.find((item) => item.id === 5)?.checked +
      '&screenshot=' + mailingLists.value.find((item) => item.id === 2)?.checked +
      '&brand=' + mailingLists.value.find((item) => item.id === 1)?.checked +
      '&screenshotColors=' + mailingLists.value.find((item) => item.id === 2)?.currentVal
    fetchData(builtUrl)
  }

  const getUrl = () => {
    // Ignore clicks while a generation is already running.
    if (isLoading.value) return

    // 1) Validate input first so users get immediate, specific feedback.
    if (urlData.value.trim() === '') {
      resetPendingVerification()
      hasWarning.value = false
      hasError.value = true
      errorMsg.value = 'Please enter a valid URL'
      return
    }

    if (!mailingLists.value.some((item) => item.checked)) {
      resetPendingVerification()
      hasWarning.value = false
      hasError.value = true
      errorMsg.value = 'Please select at least one option from the settings below'
      return
    }

    // 2) Human verification. If a token isn't ready yet, queue the request and
    //    let the token watcher continue it automatically.
    if (!token.value) {
      pendingGenerate.value = true
      hasError.value = false
      hasWarning.value = true
      errorMsg.value = "Verifying you're human — your palette will generate automatically…"
      clearTimeout(pendingHintId)
      clearTimeout(pendingTimeoutId)
      // If verification hasn't finished after a few seconds, an interactive check
      // is probably on screen — nudge the user toward it.
      pendingHintId = setTimeout(() => {
        if (pendingGenerate.value && !token.value) {
          errorMsg.value = 'Almost there — please complete the quick verification to continue.'
        }
      }, 4000)
      // Hard cap: if nothing ever resolves, stop waiting and give a real error.
      pendingTimeoutId = setTimeout(() => {
        if (pendingGenerate.value && !token.value) {
          resetPendingVerification()
          hasWarning.value = false
          hasError.value = true
          errorMsg.value =
            "We couldn't verify your browser automatically. Please complete the verification, or refresh the page and try again."
        }
      }, 25000)
      return
    }

    // 3) Token is ready → generate now.
    startGenerate()
  }

  const toggleSettings = () => {
    try {
      trackEvent('Toggle Settings')
    } catch {}
    settingToggle.value = !settingToggle.value
  }

  const toggleCheck = (mailingList: any) => {
    try {
      trackEvent('Toggle Setting Options (' + mailingList.title + ')')
    } catch {}
    mailingList.checked = !mailingList.checked
  }

  watch(token, (newVal, oldVal) => {
    if (!newVal || newVal === oldVal) return

    // A fresh, valid token arrived — clear any transient state.
    turnstileFailed.value = false
    if (hasWarning.value) hasWarning.value = false

    // Auto-continue a queued generation.
    if (pendingGenerate.value) {
      startGenerate()
      return
    }

    // Deep link support: kick off the first load once we're verified.
    if (!queryParamHandled && queryParam.value.url != null) {
      queryParamHandled = true
      const raw = String(queryParam.value.url)
      // Two shapes reach us here:
      //  1. A shared palette, where ?url= is the whole getColors request
      //     (that's what fetchData pushes into the address bar) — possibly an
      //     absolute api.colorize.design URL from an older link. Pull the target
      //     site and its flags out and re-issue through the same-origin adapter.
      //  2. A plain site, e.g. /?url=vk.com — used by the brand-colors pages and
      //     any hand-written link. Generate it with the default settings.
      let embedded: URLSearchParams | null = null
      try {
        embedded = new URL(raw, window.location.origin).searchParams
      } catch {
        embedded = null
      }
      const site = embedded?.get('url')
      if (site) {
        urlData.value = site
        const flag = (name: string, fallback = 'false') => embedded!.get(name) ?? fallback
        const builtUrl =
          baseUrl + site +
          '&html=' + flag('html') +
          '&css=' + flag('css') +
          '&js=' + flag('js') +
          '&screenshot=' + flag('screenshot') +
          '&brand=' + flag('brand', 'true') +
          '&screenshotColors=' + flag('screenshotColors', '6')
        fetchData(builtUrl)
      } else if (raw.trim() !== '') {
        urlData.value = raw.trim()
        // Reuses the current (default: everything on) extraction settings.
        startGenerate()
      }
    }
  })

  onMounted(() => {
    if (queryParam.value.url != null) {
      isLoading.value = true
      screenshotAnimation.value = true
      // A generation runs immediately for a deep link (?url=…) — start loading
      // the Lottie player now so the "Generating…" animation is ready.
      ensureLottie()
      // NOTE: the original also armed a 30s "Request timed out" fallback here,
      // but its guard compared the ref object to '' so it never fired (and its
      // handle was never cleared). Being provably inert, it is not reproduced.
    }
  })

  return {
    urlData,
    typedUrl,
    hasError,
    hasWarning,
    hasSuccess,
    errorMsg,
    isLoading,
    settingToggle,
    mailingLists,
    token,
    turnstileOptions,
    setTurnstileRef,
    pendingGenerate,
    getUrl,
    toggleSettings,
    toggleCheck,
    setError,
    feedbackTarget,
  }
}
